/**
 * TierEditor - Edit the three pricing tiers
 *
 * Simplified pricing model:
 * - Total = total_squares × price_per_square (from ManufacturerProduct)
 * - Line items are description-only (what's included)
 * - Tax is at proposal level, not tier level
 * - Manual pricing override option available
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Input,
  Button,
  IconButton,
  Table,
  Sheet,
  Chip,
  Stack,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  Divider,
  Alert,
  Tooltip,
  Autocomplete,
  AutocompleteOption,
  ListItemContent,
} from '@mui/joy';
import {
  Plus as Add,
  Trash as Delete,
  Star,
  DotsSixVertical as DragIndicator,
  Package as Inventory,
  PencilSimple as Edit,
  Calculator as Calculate,
  ArrowCounterClockwise as Reset,
} from '@phosphor-icons/react';
import { useTierLabels } from '../../hooks/useTierLabels';
import { DebouncedInput, DebouncedTextarea } from '../common/DebouncedInput';
import type {
  ProposalTier,
  ProposalLineItem,
  TierType,
  ManufacturerWithProducts,
} from '../../types/proposals';

interface ProductPickerOption {
  id: number;
  name: string;
  manufacturerId: number;
  manufacturerName: string;
  pricePerSquare: string | null;
}

interface TierEditorProps {
  tiers: ProposalTier[];
  totalSquares: number | null;
  taxEnabled: boolean;
  taxRate: number;
  productCatalog: ManufacturerWithProducts[];
  featuredManufacturerId: number | null;
  onUpdateTier: (tierId: number, data: Partial<ProposalTier>) => void;
  onSetTierProduct: (tierId: number, productId: number | null) => void;
  onAddLineItem: (tierId: number) => void;
  onUpdateLineItem: (itemId: number, data: Partial<ProposalLineItem>) => void;
  onDeleteLineItem: (itemId: number) => void;
  onReorderLineItems: (items: ProposalLineItem[]) => void;
}

// DRF serializes decimals as strings, so every money value has to be coerced
// before arithmetic or `+` concatenates instead of adding.
const toNumber = (value: number | string | null | undefined): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const tierColors: Record<TierType, 'neutral' | 'primary' | 'success'> = {
  good: 'neutral',
  better: 'primary',
  best: 'success',
};


export const TierEditor: React.FC<TierEditorProps> = ({
  tiers,
  totalSquares,
  taxEnabled,
  taxRate,
  productCatalog,
  featuredManufacturerId,
  onUpdateTier,
  onSetTierProduct,
  onAddLineItem,
  onUpdateLineItem,
  onDeleteLineItem,
  onReorderLineItems,
}) => {
  const { labels: tierLabels } = useTierLabels();
  const [expandedChoice, setExpandedTier] = useState<number | null | undefined>(undefined);
  const expandedTier = expandedChoice === undefined
    ? tiers.find((t) => t.is_recommended)?.id ?? tiers[0]?.id ?? null
    : expandedChoice;
  const [editingSubtotal, setEditingSubtotal] = useState<number | null>(null);
  const [manualSubtotalValue, setManualSubtotalValue] = useState<string>('');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [draggingItemId, setDraggingItemId] = useState<number | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<number | null>(null);

  const sortedTiers = [...tiers].sort((a, b) => a.sort_order - b.sort_order);
  const numericTaxRate = toNumber(taxRate);

  // Flat product list for the per-tier picker, featured manufacturer first
  const productOptions = useMemo<ProductPickerOption[]>(() => {
    const sortedCatalog = [...productCatalog].sort((a, b) => {
      if (a.id === featuredManufacturerId) return -1;
      if (b.id === featuredManufacturerId) return 1;
      return a.name.localeCompare(b.name);
    });
    return sortedCatalog.flatMap((mfr) =>
      mfr.products.map((p) => ({
        id: p.id,
        name: p.name,
        manufacturerId: mfr.id,
        manufacturerName: mfr.name,
        pricePerSquare: p.pricing?.price_per_square ?? null,
      }))
    );
  }, [productCatalog, featuredManufacturerId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const handleStartEditSubtotal = (tier: ProposalTier, calculatedSubtotal: number) => {
    setEditingSubtotal(tier.id);
    setManualSubtotalValue(
      tier.use_manual_pricing && tier.subtotal != null
        ? tier.subtotal.toString()
        : calculatedSubtotal.toString()
    );
    setPriceError(null);
  };

  const handleSaveManualSubtotal = (tier: ProposalTier) => {
    const value = parseFloat(manualSubtotalValue);
    if (!Number.isFinite(value) || value < 0) {
      setPriceError('Please enter a valid price');
      return;
    }

    // Check minimum price
    const product = tier.manufacturer_product_details;
    const minPricePerSquare = product?.minimum_price_per_square
      ? parseFloat(product.minimum_price_per_square)
      : null;

    if (minPricePerSquare && totalSquares && !tier.can_override_minimum_price) {
      const minSubtotal = minPricePerSquare * totalSquares;
      if (value < minSubtotal) {
        setPriceError(
          `Price cannot be below minimum (${formatCurrency(minSubtotal)} based on $${minPricePerSquare}/sq × ${totalSquares} sq). Ask an Org Admin to approve a lower price.`
        );
        return;
      }
    }

    onUpdateTier(tier.id, {
      use_manual_pricing: true,
      subtotal: value,
    });
    setEditingSubtotal(null);
    setPriceError(null);
  };

  const handleRevertToCalculated = (tier: ProposalTier) => {
    onUpdateTier(tier.id, {
      use_manual_pricing: false,
    });
    setEditingSubtotal(null);
    setPriceError(null);
  };

  const handleLineItemDrop = (items: ProposalLineItem[], targetId: number) => {
    if (draggingItemId === null || draggingItemId === targetId) {
      setDraggingItemId(null);
      setDragOverItemId(null);
      return;
    }
    const fromIndex = items.findIndex((i) => i.id === draggingItemId);
    const toIndex = items.findIndex((i) => i.id === targetId);
    setDraggingItemId(null);
    setDragOverItemId(null);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onReorderLineItems(reordered);
  };

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 2 }}>
        Pricing Tiers
      </Typography>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
        {tierLabels.good}, {tierLabels.better}, and {tierLabels.best} pricing options. Total is
        calculated from squares × price per square.
      </Typography>

      {!totalSquares && (
        <Alert color="warning" sx={{ mb: 2 }}>
          Enter total squares in Proposal Settings to calculate pricing.
        </Alert>
      )}

      <Stack spacing={2}>
        {sortedTiers.map((tier) => {
          const product = tier.manufacturer_product_details;
          const pricePerSquare = product?.price_per_square ? parseFloat(product.price_per_square) : null;
          const calculatedSubtotal = totalSquares && pricePerSquare ? toNumber(totalSquares) * pricePerSquare : 0;
          const calculatedTax = taxEnabled ? calculatedSubtotal * (numericTaxRate / 100) : 0;
          const calculatedTotal = calculatedSubtotal + calculatedTax;

          // Calculate actual total when using manual pricing
          const hasManualSubtotal = tier.use_manual_pricing && tier.subtotal != null;
          const manualSubtotal = toNumber(tier.subtotal);
          const minimumSubtotal = toNumber(product?.minimum_price_per_square) * toNumber(totalSquares);
          const proposedSubtotal = editingSubtotal === tier.id ? Number(manualSubtotalValue) : manualSubtotal;
          const belowMinimum = minimumSubtotal > 0 && (editingSubtotal === tier.id || hasManualSubtotal)
            && Number.isFinite(proposedSubtotal) && proposedSubtotal >= 0 && proposedSubtotal < minimumSubtotal;
          const manualTax = taxEnabled ? manualSubtotal * (numericTaxRate / 100) : 0;
          const actualTotal = hasManualSubtotal
            ? manualSubtotal + manualTax
            : toNumber(tier.total_amount) || calculatedTotal;

          // Determine if this is a discount (manual price lower than calculated)
          const isDiscounted = tier.use_manual_pricing &&
            calculatedTotal > 0 &&
            actualTotal < calculatedTotal;

          // Calculate savings amount
          const savings = isDiscounted ? calculatedTotal - actualTotal : 0;

          const lineItems = [...(tier.line_items || [])].sort((a, b) => a.sort_order - b.sort_order);

          return (
            <Card
              key={tier.id}
              variant="outlined"
              sx={{
                borderColor: expandedTier === tier.id ? `${tierColors[tier.tier_type]}.500` : 'divider',
                borderWidth: expandedTier === tier.id ? 2 : 1,
              }}
            >
              {/* Tier Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'background.level1' },
                }}
                onClick={() => setExpandedTier(expandedTier === tier.id ? null : tier.id)}
              >
                <Chip
                  size="sm"
                  color={tierColors[tier.tier_type]}
                  variant="solid"
                >
                  {tier.tier_type_label || tierLabels[tier.tier_type]}
                </Chip>

                <Box sx={{ flex: 1 }}>
                  <DebouncedInput
                    size="sm"
                    variant="plain"
                    value={tier.name}
                    onClick={(e) => e.stopPropagation()}
                    onCommit={(name) => onUpdateTier(tier.id, { name })}
                    placeholder="Tier name (e.g., Architectural Shingles)"
                    sx={{ fontWeight: 'lg' }}
                  />
                </Box>

                <Button
                  size="sm"
                  variant="plain"
                  aria-expanded={expandedTier === tier.id}
                  aria-label={`${expandedTier === tier.id ? 'Hide' : 'View'} ${tier.name} package details`}
                  onClick={(event) => { event.stopPropagation(); setExpandedTier(expandedTier === tier.id ? null : tier.id); }}
                >
                  {expandedTier === tier.id ? 'Hide details' : 'View details'} · {lineItems.length} included items
                </Button>
                {tier.is_recommended && (
                  <Chip
                    size="sm"
                    color="warning"
                    variant="soft"
                    startDecorator={<Star size={18} />}
                  >
                    Recommended
                  </Chip>
                )}

                <Box sx={{ textAlign: 'right' }}>
                  {isDiscounted && (
                    <Typography
                      level="body-sm"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'danger.500',
                      }}
                    >
                      {formatCurrency(calculatedTotal)}
                    </Typography>
                  )}
                  <Typography level="title-md" fontWeight="lg">
                    {formatCurrency(actualTotal)}
                  </Typography>
                </Box>
              </Box>

              {/* Expanded Content */}
              {expandedTier === tier.id && (
                <CardContent sx={{ pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Product & Pricing Info */}
                  <Card variant="soft" size="sm" sx={{ mb: 3 }}>
                    <Stack spacing={1}>
                      <FormControl>
                        <FormLabel>Product</FormLabel>
                        <Autocomplete
                          size="sm"
                          placeholder="Featured manufacturer default"
                          options={productOptions}
                          groupBy={(option) => option.manufacturerName}
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={
                            productOptions.find((o) => o.id === tier.manufacturer_product) || null
                          }
                          onChange={(_, option) =>
                            onSetTierProduct(tier.id, option ? option.id : null)
                          }
                          renderOption={(props, option) => (
                            <AutocompleteOption {...props} key={option.id}>
                              <ListItemContent>
                                {option.name}
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                  {option.pricePerSquare
                                    ? `$${parseFloat(option.pricePerSquare).toFixed(2)}/sq`
                                    : 'No pricing set'}
                                </Typography>
                              </ListItemContent>
                            </AutocompleteOption>
                          )}
                        />
                        <FormHelperText>
                          Pick any manufacturer's product for this tier. Clearing the selection
                          reverts to the featured manufacturer's default.
                        </FormHelperText>
                      </FormControl>

                      {tier.product_override && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            size="sm"
                            color="warning"
                            variant="soft"
                            startDecorator={<Edit size={14} />}
                          >
                            Manual product override
                            {product?.manufacturer_name ? ` — ${product.manufacturer_name}` : ''}
                          </Chip>
                          <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            startDecorator={<Reset size={16} />}
                            onClick={() => onSetTierProduct(tier.id, null)}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Use featured manufacturer default
                          </Button>
                        </Box>
                      )}

                      {product ? (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Inventory size={18} color="var(--joy-palette-text-secondary)" />
                            <Typography level="title-sm">
                              {product.name}
                            </Typography>
                            {product.warranty_years && (
                              <Chip size="sm" variant="outlined">
                                {product.warranty_years}yr warranty
                              </Chip>
                            )}
                          </Box>
                          {pricePerSquare ? (
                            <>
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                {totalSquares ? `${totalSquares} sq` : '? sq'} × ${pricePerSquare.toFixed(2)}/sq
                                = {formatCurrency(calculatedSubtotal)}
                                {taxEnabled && ` + ${numericTaxRate}% tax = ${formatCurrency(calculatedTotal)}`}
                              </Typography>
                              {tier.use_manual_pricing && (
                                <Chip
                                  size="sm"
                                  color="warning"
                                  variant="soft"
                                  startDecorator={<Edit size={18} />}
                                >
                                  Manual price override active
                                </Chip>
                              )}
                            </>
                          ) : (
                            <Alert size="sm" color="warning">
                              Set pricing for this product in Default Pricing tab
                            </Alert>
                          )}
                        </>
                      ) : (
                        <Alert size="sm" color="neutral">
                          <Box>
                            <Typography level="body-sm" fontWeight="md">No product assigned</Typography>
                            <Typography level="body-xs">
                              Select a featured manufacturer to auto-assign products, or pick a
                              product for this tier above.
                            </Typography>
                          </Box>
                        </Alert>
                      )}
                    </Stack>
                  </Card>

                  {/* Tier Settings */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Description</FormLabel>
                      <DebouncedTextarea
                        size="sm"
                        minRows={2}
                        value={tier.description}
                        onCommit={(description) => onUpdateTier(tier.id, { description })}
                        placeholder="Brief description of this tier..."
                      />
                    </FormControl>

                    <Box sx={{ width: { xs: '100%', sm: 150 } }}>
                      <FormControl>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            size="sm"
                            checked={tier.is_recommended}
                            onChange={(e) => onUpdateTier(tier.id, { is_recommended: e.target.checked })}
                          />
                          <Typography level="body-sm">Recommended</Typography>
                        </Stack>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Advantages */}
                  <Box sx={{ mb: 3 }}>
                    <FormLabel sx={{ mb: 1 }}>Advantages (shown as bullet points)</FormLabel>
                    <Stack spacing={1}>
                      {(tier.advantages || []).map((advantage, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                          <DebouncedInput
                            size="sm"
                            value={advantage}
                            onCommit={(text) => {
                              const newAdvantages = [...(tier.advantages || [])];
                              newAdvantages[index] = text;
                              onUpdateTier(tier.id, { advantages: newAdvantages });
                            }}
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => {
                              const newAdvantages = tier.advantages.filter((_, i) => i !== index);
                              onUpdateTier(tier.id, { advantages: newAdvantages });
                            }}
                          >
                            <Delete size={18} />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="sm"
                        variant="plain"
                        startDecorator={<Add />}
                        onClick={() => {
                          onUpdateTier(tier.id, {
                            advantages: [...(tier.advantages || []), ''],
                          });
                        }}
                      >
                        Add Advantage
                      </Button>
                    </Stack>
                  </Box>

                  {/* Line Items - Description Only */}
                  <Box>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Line Items (What's Included)
                    </Typography>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden', mb: 2 }}>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: 30 }}></th>
                            <th>Description</th>
                            <th style={{ width: 40 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item) => (
                            <tr
                              key={item.id}
                              onDragOver={(e) => {
                                e.preventDefault();
                                if (dragOverItemId !== item.id) setDragOverItemId(item.id);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                handleLineItemDrop(lineItems, item.id);
                              }}
                              style={{
                                opacity: draggingItemId === item.id ? 0.4 : 1,
                                borderTop:
                                  dragOverItemId === item.id && draggingItemId !== item.id
                                    ? '2px solid var(--joy-palette-primary-500)'
                                    : '2px solid transparent',
                              }}
                            >
                              <td>
                                <Box
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/plain', String(item.id));
                                    setDraggingItemId(item.id);
                                  }}
                                  onDragEnd={() => {
                                    setDraggingItemId(null);
                                    setDragOverItemId(null);
                                  }}
                                  sx={{ display: 'flex', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                                >
                                  <DragIndicator size={18} color="var(--joy-palette-text-tertiary)" />
                                </Box>
                              </td>
                              <td>
                                <DebouncedInput
                                  size="sm"
                                  variant="plain"
                                  value={item.description}
                                  onCommit={(description) => onUpdateLineItem(item.id, { description })}
                                  placeholder="Line item description..."
                                />
                              </td>
                              <td>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="danger"
                                  onClick={() => onDeleteLineItem(item.id)}
                                >
                                  <Delete size={18} />
                                </IconButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Sheet>

                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<Add />}
                      onClick={() => onAddLineItem(tier.id)}
                    >
                      Add Line Item
                    </Button>
                  </Box>

                  {/* Totals */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Card variant="soft" size="sm" sx={{ minWidth: 280 }}>
                      <Stack spacing={0.5}>
                        {/* Editable Subtotal */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography level="body-sm">Subtotal:</Typography>
                          {editingSubtotal === tier.id ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Input
                                size="sm"
                                type="number"
                                value={manualSubtotalValue}
                                slotProps={{ input: { 'aria-label': `Subtotal for ${tier.name}`, min: 0, step: '0.01' } }}
                                onChange={(e) => setManualSubtotalValue(e.target.value)}
                                startDecorator="$"
                                sx={{ width: 120 }}
                                error={!!priceError}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveManualSubtotal(tier);
                                  if (e.key === 'Escape') {
                                    setEditingSubtotal(null);
                                    setPriceError(null);
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                variant="solid"
                                color="primary"
                                onClick={() => handleSaveManualSubtotal(tier)}
                              >
                                {belowMinimum && tier.can_override_minimum_price ? 'Save admin override' : 'Save'}
                              </Button>
                              <Button
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => {
                                  setEditingSubtotal(null);
                                  setPriceError(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Tooltip title="Click to override price" placement="left">
                              <Typography
                                level="body-sm"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'var(--brand-text, #7013f5)',
                                    textDecoration: 'underline',
                                  },
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={`Edit subtotal for ${tier.name}`}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleStartEditSubtotal(tier, calculatedSubtotal);
                                  }
                                }}
                                onClick={() => handleStartEditSubtotal(tier, calculatedSubtotal)}
                              >
                                {formatCurrency(hasManualSubtotal ? manualSubtotal : calculatedSubtotal)}
                                {tier.use_manual_pricing && (
                                  <Edit size={14} style={{ marginLeft: 4 }} />
                                )}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                        {belowMinimum && (editingSubtotal !== tier.id || tier.can_override_minimum_price) && (
                          <Alert size="sm" color="warning" sx={{ maxWidth: 380 }}>
                            {editingSubtotal === tier.id
                              ? `Org Admin override: you can save below the ${formatCurrency(minimumSubtotal)} minimum. This applies only to this proposal; the organization's minimum stays unchanged.`
                              : `This proposal's price is below the ${formatCurrency(minimumSubtotal)} minimum. Org Admin approval is required to change it to another price below the minimum.`}
                          </Alert>
                        )}
                        {priceError && editingSubtotal === tier.id && (
                          <Alert size="sm" color="danger" sx={{ py: 0.5 }}>
                            {priceError}
                          </Alert>
                        )}
                        {tier.use_manual_pricing && editingSubtotal !== tier.id && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="sm"
                              variant="plain"
                              color="neutral"
                              startDecorator={<Calculate size={18} />}
                              onClick={() => handleRevertToCalculated(tier)}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Revert to calculated
                            </Button>
                          </Box>
                        )}
                        {taxEnabled && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography level="body-sm">Tax ({numericTaxRate}%):</Typography>
                            <Typography level="body-sm">
                              {formatCurrency(hasManualSubtotal ? manualTax : calculatedTax)}
                            </Typography>
                          </Box>
                        )}
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <Typography level="title-sm">Total:</Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            {isDiscounted && (
                              <Typography
                                level="body-sm"
                                sx={{
                                  textDecoration: 'line-through',
                                  color: 'danger.500',
                                }}
                              >
                                {formatCurrency(calculatedTotal)}
                              </Typography>
                            )}
                            <Typography level="title-sm" fontWeight="lg">
                              {formatCurrency(actualTotal)}
                            </Typography>
                          </Box>
                        </Box>
                        {isDiscounted && savings > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Chip size="sm" color="success" variant="soft">
                              Save {formatCurrency(savings)}
                            </Chip>
                          </Box>
                        )}
                      </Stack>
                    </Card>
                  </Box>
                </CardContent>
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};
